import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Building2, User, Loader2, CheckCircle, ArrowRight, Search, Trash2 } from "lucide-react";

const BUSINESS_KEYWORDS = [
  'LLC', 'L.L.C.', 'INC', 'CORP', 'CORPORATION', 'LTD', 'L.P.', 'LP',
  'TRUST', 'TRUSTEE', 'TRUSTEES', 'REVOCABLE', 'IRREVOCABLE', 'LIVING TRUST',
  'FAMILY TRUST', 'ESTATE', 'PARTNERSHIP', 'ASSOCIATES', 'HOLDINGS',
  'PROPERTIES', 'REALTY', 'ENTERPRISES', 'VENTURES', 'GROUP', 'COMPANY',
  'INVESTMENTS', 'MANAGEMENT', 'CAPITAL', 'FUND', 'FOUNDATION',
  'ASSOCIATION', 'PARTNERS', 'DBA',
];

interface PotentialBusiness {
  id: string;
  first_name: string | null;
  last_name: string | null;
  business_name: string | null;
  county: string | null;
  origin_state: string | null;
  street_address: string | null;
  matchedKeyword: string;
  fullName: string;
}

interface BusinessEntityReviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
}

export function BusinessEntityReview({ open, onOpenChange, onComplete }: BusinessEntityReviewProps) {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<PotentialBusiness[]>([]);
  const [processing, setProcessing] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      scanForBusinesses();
    }
  }, [open]);

  const scanForBusinesses = async () => {
    setLoading(true);
    try {
      // Fetch records that DON'T have business_name set
      let allRecords: any[] = [];
      let page = 0;
      const pageSize = 1000;

      while (true) {
        const { data, error } = await supabase
          .from("mailing_records")
          .select("id, first_name, last_name, business_name, county, origin_state, street_address")
          .or("business_name.is.null,business_name.eq.")
          .range(page * pageSize, (page + 1) * pageSize - 1);

        if (error || !data || data.length === 0) break;
        allRecords = [...allRecords, ...data];
        if (data.length < pageSize) break;
        page++;
      }

      // Check each record's name fields for business keywords
      const potentials: PotentialBusiness[] = [];
      for (const r of allRecords) {
        const fullName = `${r.first_name || ''} ${r.last_name || ''}`.trim().toUpperCase();
        if (!fullName) continue;

        for (const keyword of BUSINESS_KEYWORDS) {
          // Match whole word or at end of string
          const regex = new RegExp(`\\b${keyword.replace(/\./g, '\\.')}\\b`, 'i');
          if (regex.test(fullName)) {
            potentials.push({
              ...r,
              matchedKeyword: keyword,
              fullName: `${r.first_name || ''} ${r.last_name || ''}`.trim(),
            });
            break;
          }
        }
      }

      setRecords(potentials);
      if (potentials.length === 0) {
        toast.info("No potential business entities found in individual records");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to scan records");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsBusiness = async (record: PotentialBusiness) => {
    setProcessing(prev => new Set(prev).add(record.id));
    try {
      // Move the combined name to business_name, clear first/last
      const businessName = record.fullName;
      const { error } = await supabase
        .from("mailing_records")
        .update({
          business_name: businessName,
          first_name: null,
          last_name: null,
        })
        .eq("id", record.id);

      if (error) throw error;
      toast.success(`Marked "${businessName}" as business`);
      setRecords(prev => prev.filter(r => r.id !== record.id));
    } catch (err) {
      toast.error("Failed to update record");
    } finally {
      setProcessing(prev => { const n = new Set(prev); n.delete(record.id); return n; });
    }
  };

  const handleKeepAsIndividual = (record: PotentialBusiness) => {
    setRecords(prev => prev.filter(r => r.id !== record.id));
  };

  const handleDelete = async (record: PotentialBusiness) => {
    setProcessing(prev => new Set(prev).add(record.id));
    try {
      const { error } = await supabase
        .from("mailing_records")
        .delete()
        .eq("id", record.id);

      if (error) throw error;
      toast.success(`Deleted "${record.fullName}"`);
      setRecords(prev => prev.filter(r => r.id !== record.id));
    } catch (err) {
      toast.error("Failed to delete record");
    } finally {
      setProcessing(prev => { const n = new Set(prev); n.delete(record.id); return n; });
    }
  };

  const handleMarkAllAsBusiness = async () => {
    if (records.length === 0) return;
    const toProcess = [...records];
    let successCount = 0;

    for (const record of toProcess) {
      try {
        const { error } = await supabase
          .from("mailing_records")
          .update({
            business_name: record.fullName,
            first_name: null,
            last_name: null,
          })
          .eq("id", record.id);

        if (!error) successCount++;
      } catch {
        // skip
      }
    }

    toast.success(`Marked ${successCount} records as businesses`);
    setRecords([]);
  };

  const handleClose = () => {
    onOpenChange(false);
    if (records.length === 0) {
      onComplete();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); else onOpenChange(o); }}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Review Potential Business Entities
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            These records don't have a business name but contain business-like keywords in their name fields.
            Choose to flag as business or keep as individual.
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2 text-muted-foreground">Scanning records...</span>
          </div>
        ) : records.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="h-12 w-12 text-primary mx-auto mb-3" />
            <p className="font-medium">All clear!</p>
            <p className="text-sm text-muted-foreground">No potential business entities found in individual records.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <Badge variant="secondary">{records.length} potential business records</Badge>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setRecords([])}>
                  Dismiss All (Keep as Individual)
                </Button>
                <Button variant="default" size="sm" onClick={handleMarkAllAsBusiness}>
                  <Building2 className="h-4 w-4 mr-1" />
                  Mark All as Business
                </Button>
              </div>
            </div>

            <div className="overflow-auto flex-1">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Current Name</TableHead>
                    <TableHead>Keyword</TableHead>
                    <TableHead>County</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map(record => (
                    <TableRow key={record.id}>
                      <TableCell className="font-medium">
                        <div>
                          <span>{record.fullName}</span>
                          {record.first_name && record.last_name && (
                            <span className="text-xs text-muted-foreground block">
                              First: {record.first_name} | Last: {record.last_name}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{record.matchedKeyword}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{record.county || '—'}</TableCell>
                      <TableCell className="text-sm">{record.origin_state || '—'}</TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleKeepAsIndividual(record)}
                          disabled={processing.has(record.id)}
                        >
                          <User className="h-3.5 w-3.5 mr-1" />
                          Keep
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleMarkAsBusiness(record)}
                          disabled={processing.has(record.id)}
                        >
                          {processing.has(record.id) ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <>
                              <Building2 className="h-3.5 w-3.5 mr-1" />
                              Business
                            </>
                          )}
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(record)}
                          disabled={processing.has(record.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1" />
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
